import { WebMercatorViewport } from "@deck.gl/core";
import DeckGL, { type DeckGLRef } from "@deck.gl/react";
import type {
    Feature,
    FeatureCollection,
} from "@deck.gl-community/editable-layers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faLocationArrow } from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import area from "@turf/area";
import bbox from "@turf/bbox";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import MapGL from "react-map-gl/maplibre";
import { useMapApiClient } from "../../../../../hooks";
import { AlignedDataDisplay } from "../../../../../ui/GenericFields";
import { FullscreenModal } from "../../../../../ui/Modals";
import { useResizeObserver } from "../../../../../ui/Plot/hooks";
import { formatDateTime } from "../../../../../utils/dates";
import { useDeckDeviceRelease } from "../../../../../utils/webgl";
import { basemaps } from "../../../basemaps";
import type { MapViewState } from "../../../CustomTypes";
import { useMapData } from "../../../hooks/mapDataAndFilters";
import { ChampionX3DPointsLayer } from "../../../layers/extra";
import { SidebarSection } from "../../../ui/SidebarItems";

// maxPitch must also be passed to MapGL, or the basemap clamps to 60 and
// drifts out of sync with the points.
const MAX_PITCH = 85;
const DEFAULT_CAMERA = { pitch: 60, bearing: 0, maxPitch: MAX_PITCH };

/**
 * Renders the survey points in 3D over satellite imagery.
 *
 * Drag pans, shift+drag rotates/tilts. Points are colored matching main map.
 */
const ChampionX3DView = ({ surveyData }: { surveyData: FeatureCollection }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const size = useResizeObserver(containerRef);
    const deckRef = useRef<DeckGLRef>(null);
    const deckDeviceRelease = useDeckDeviceRelease(deckRef);

    // Point features with altitude (3rd coord) normalized to the lowest
    // measurement, so the cloud rests on the ground plane. Points without an
    // altitude naturally fall to z = 0.
    const points = useMemo(() => {
        const feats = (surveyData?.features ?? []).filter(
            (f) => f.geometry?.type === "Point",
        );
        const altFloor = feats.reduce(
            (min, f) => Math.min(min, (f.geometry as any).coordinates[2] ?? 0),
            Infinity,
        );
        return feats.map((f) => {
            const [lng, lat, alt] = (f.geometry as any).coordinates;
            return {
                ...f,
                // Keep the true altitude for display; plot z is normalized.
                properties: { ...f.properties, altitude: alt },
                geometry: {
                    ...f.geometry,
                    coordinates: [lng, lat, (alt ?? 0) - altFloor],
                },
            } as Feature;
        });
    }, [surveyData]);

    const bounds = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = bbox({
            type: "FeatureCollection",
            features: points,
        });
        return [
            [minLng, minLat],
            [maxLng, maxLat],
        ] as [[number, number], [number, number]];
    }, [points]);

    // Framing (center + zoom) fitted to the points and the container.
    const fittedView: Pick<
        MapViewState,
        "longitude" | "latitude" | "zoom"
    > | null = useMemo(() => {
        if (
            size.width === 0 ||
            size.height === 0 ||
            !Number.isFinite(bounds[0][0])
        ) {
            return null;
        }
        const vp = new WebMercatorViewport({
            width: size.width,
            height: size.height,
        });
        // A tiny span (single / co-located points) breaks fitBounds.
        const spanLng = bounds[1][0] - bounds[0][0];
        const spanLat = bounds[1][1] - bounds[0][1];
        if (spanLng < 1e-6 && spanLat < 1e-6) {
            return {
                longitude: bounds[0][0],
                latitude: bounds[0][1],
                zoom: 16,
            };
        }
        const { longitude, latitude, zoom } = vp.fitBounds(bounds, {
            padding: 24,
        });
        // Nudge in past the fit so points sit close by default.
        return { longitude, latitude, zoom: zoom + 0.2 };
    }, [bounds, size]);

    const [viewState, setViewState] = useState<MapViewState>({
        longitude: 0,
        latitude: 0,
        zoom: 1,
        ...DEFAULT_CAMERA,
    });

    // Re-frame when the fit changes (data or container size); keep the
    // user's pitch/bearing.
    useLayoutEffect(() => {
        if (!fittedView) return;
        setViewState((vs) => ({ ...vs, ...fittedView }));
    }, [fittedView]);

    const layers = useMemo(
        () => ChampionX3DPointsLayer(points, "ch4"),
        [points],
    );

    return (
        <div ref={containerRef} className="relative w-full h-full">
            <DeckGL
                ref={deckRef}
                {...deckDeviceRelease}
                viewState={viewState as MapViewState}
                onViewStateChange={({ viewState: vs }) =>
                    setViewState(vs as MapViewState)
                }
                controller={true}
                layers={layers}
                getCursor={({ isDragging, isHovering }) =>
                    isDragging ? "grabbing" : isHovering ? "pointer" : "grab"
                }
                getTooltip={({ object }: any) => {
                    if (!object) return null;
                    const co2 = object.properties?.co2;
                    const ch4 = object.properties?.ch4;
                    const altitude = object.properties?.altitude;
                    return {
                        html: `<div style="font-size:11px;line-height:1.4">
                            <div><b>CO₂:</b> ${
                                typeof co2 === "number"
                                    ? `${co2.toFixed(2)} ppm`
                                    : "-"
                            }</div>
                            <div><b>CH₄:</b> ${
                                typeof ch4 === "number"
                                    ? `${(ch4 * 1000).toFixed(2)} ppb`
                                    : "-"
                            }</div>
                            <div><b>Altitude:</b> ${
                                typeof altitude === "number"
                                    ? `${altitude.toFixed(1)} m`
                                    : "-"
                            }</div>
                        </div>`,
                        style: {
                            background: "rgba(255,255,255,0.95)",
                            color: "#1f2937",
                            borderRadius: "4px",
                            padding: "4px 6px",
                        },
                    };
                }}
            >
                <MapGL
                    mapStyle={basemaps.esri.mapStyle as any}
                    attributionControl={false}
                    maxPitch={MAX_PITCH}
                />
            </DeckGL>

            <div className="absolute top-2 left-2 flex items-center gap-2">
                <button
                    type="button"
                    title="Reset view"
                    disabled={!fittedView}
                    onClick={() =>
                        fittedView &&
                        setViewState({ ...fittedView, ...DEFAULT_CAMERA })
                    }
                    className=" z-10 rounded bg-white/90 py-0.5 px-1.5 shadow border hover:bg-white"
                >
                    <FontAwesomeIcon
                        icon={faLocationArrow}
                        className="w-4 text-ae-blue-550"
                    />
                </button>
            </div>

            <div className="absolute bottom-0 left-0 flex items-center gap-2">
                <div className="bg-white bg-opacity-90 border rounded-tr text-xs px-2 text-ae-slate-600">
                    Drag to pan, shift+drag to rotate/tilt.
                </div>
            </div>
        </div>
    );
};

/** {@link ChampionX3DView} with an expand button that opens it in a fullscreen modal. */
const ChampionX3DPanel = ({
    surveyData,
}: {
    surveyData: FeatureCollection;
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <div className="relative w-full h-full">
                {/* One view at a time: each costs a WebGL context. */}
                {!expanded && <ChampionX3DView surveyData={surveyData} />}
                <div className="absolute top-2 right-2 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        title="Expand"
                        className=" z-10 rounded bg-white/90 py-0.5 px-1.5 shadow border hover:bg-white"
                    >
                        <FontAwesomeIcon
                            icon={faExpand}
                            className="w-4 text-ae-blue-550"
                        />
                    </button>
                </div>
            </div>

            <FullscreenModal
                title="3D Survey View"
                visible={expanded}
                onClose={() => setExpanded(false)}
            >
                <div className="flex-1 min-h-0 mt-4 overflow-hidden rounded">
                    {expanded && <ChampionX3DView surveyData={surveyData} />}
                </div>
            </FullscreenModal>
        </>
    );
};

/**
 * ExtraDataDetailsTab
 *
 * FIXME: This always assumes `Champion X` data.
 */
export const ExtraDataDetailsTab = () => {
    const { selectedContext } = useMapData("mainMap");
    const apiClient = useMapApiClient();

    // Emission information
    const extraDataDetails = useQuery({
        queryKey: ["extraData", selectedContext.extraDataId],
        queryFn: async () => {
            return await apiClient.mapExtraDataSurveysRetrieve({
                id: selectedContext.extraDataId,
            });
        },
        enabled: !!selectedContext.extraDataId,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });

    const area_sq_km = useMemo(() => {
        if (!extraDataDetails.data?.shape) return "-";
        return (
            area({
                type: "Feature",
                geometry: extraDataDetails.data.shape,
            } as Feature) / 1_000_000
        ).toFixed(2);
    }, [extraDataDetails.data?.shape]);

    // Summary stats come from the provider report (DataSurvey.metadata),
    // rather than being computed from the raw point cloud.
    const { windSpeed, windDirection } = useMemo(() => {
        const meta = extraDataDetails.data?.metadata as
            | Record<string, string>
            | undefined;
        return {
            windSpeed: meta?.wind_speed_ms ? `${meta.wind_speed_ms} m/s` : "-",
            windDirection: meta?.wind_dir_deg ? `${meta.wind_dir_deg}°` : "-",
        };
    }, [extraDataDetails.data?.metadata]);

    const { ch4Emission, co2Emission } = useMemo(() => {
        const meta = extraDataDetails.data?.metadata as
            | Record<string, string>
            | undefined;

        // Emission rate ± uncertainty, or a note when within uncertainty of zero.
        const format = (
            value: string | undefined,
            uncertainty: string | undefined,
            withinZero: string | undefined,
            unit: string,
        ) => {
            if (withinZero === "true") return "Within uncertainty of zero";
            if (!value) return "-";
            return uncertainty
                ? `${value} ± ${uncertainty} ${unit}`
                : `${value} ${unit}`;
        };

        return {
            ch4Emission: format(
                meta?.ch4_kgh,
                meta?.ch4_uncertainty_kgh,
                meta?.ch4_within_uncertainty_of_zero,
                "kg/h",
            ),
            co2Emission: format(
                meta?.co2_tonh,
                meta?.co2_uncertainty_tonh,
                meta?.co2_within_uncertainty_of_zero,
                "t/h",
            ),
        };
    }, [extraDataDetails.data?.metadata]);

    return (
        <div className="w-[400px] h-full bg-white">
            <SidebarSection
                title="Properties"
                loading={extraDataDetails.isLoading || !extraDataDetails.data}
            >
                {!!extraDataDetails.data && (
                    <AlignedDataDisplay
                        data={[
                            {
                                label: "Provider",
                                value:
                                    extraDataDetails.data.dataType ===
                                    "CHAMPION_X"
                                        ? "Champion X"
                                        : "-",
                            },
                            {
                                label: "Description",
                                value: extraDataDetails.data.description,
                            },
                            {
                                label: "Site type",
                                value:
                                    extraDataDetails.data.metadata?.site_type ||
                                    "-",
                            },
                            {
                                label: "Aircraft",
                                value:
                                    extraDataDetails.data.metadata?.aircraft ||
                                    "-",
                            },
                            {
                                label: "Start datetime",
                                value: formatDateTime(
                                    extraDataDetails.data.startDatetime,
                                ),
                            },
                            {
                                label: "End datetime",
                                value: formatDateTime(
                                    extraDataDetails.data.endDatetime,
                                ),
                            },
                            {
                                label: "Covered area",
                                value: `${area_sq_km} km2`,
                            },
                            {
                                label: "Measured points",
                                value: extraDataDetails.data.surveyData
                                    ?.features.length,
                            },
                            ...(extraDataDetails.data.metadata?.notes
                                ? [
                                      {
                                          label: "Notes",
                                          value: extraDataDetails.data.metadata
                                              .notes,
                                      },
                                  ]
                                : []),
                        ]}
                    />
                )}
            </SidebarSection>

            <SidebarSection
                title="Weather"
                loading={extraDataDetails.isLoading || !extraDataDetails.data}
            >
                {!!extraDataDetails.data && (
                    <AlignedDataDisplay
                        data={[
                            {
                                label: "Wind Speed",
                                value: windSpeed,
                            },
                            {
                                label: "Wind Direction",
                                value: windDirection,
                            },
                        ]}
                    />
                )}
            </SidebarSection>

            <SidebarSection
                title="Emissions"
                loading={extraDataDetails.isLoading || !extraDataDetails.data}
            >
                {!!extraDataDetails.data && (
                    <AlignedDataDisplay
                        data={[
                            {
                                label: "CH₄",
                                value: ch4Emission,
                            },
                            {
                                label: "CO₂",
                                value: co2Emission,
                            },
                        ]}
                    />
                )}
            </SidebarSection>

            <SidebarSection
                title="3D View"
                loading={extraDataDetails.isLoading || !extraDataDetails.data}
            >
                <div className="relative w-full h-72 overflow-hidden rounded">
                    {!!extraDataDetails.data?.surveyData && (
                        <ChampionX3DPanel
                            surveyData={
                                extraDataDetails.data
                                    .surveyData as FeatureCollection
                            }
                        />
                    )}
                </div>
            </SidebarSection>
        </div>
    );
};
